import axios from 'axios';
import { getApiBaseUrl } from '../config/apiUrl';

const API_URL = getApiBaseUrl();

// Flag to prevent multiple redirects
let isRedirecting = false;

// Axios instance oluştur
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - token ekle
api.interceptors.request.use(
  (config) => {
    // Don't add token for public endpoints
    const isPublicEndpoint = config.url?.includes('/public/') || 
                            config.url?.includes('/quotes/public/');
    const isAuthFlowEndpoint = config.url?.includes('/auth/login') ||
                              config.url?.includes('/auth/register') ||
                              config.url?.includes('/auth/2fa/verify');

    if (!isPublicEndpoint && !isAuthFlowEndpoint) {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - hata yönetimi
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect to login if we're on a public page or making a public API call
      const isPublicPage = window.location.pathname.startsWith('/public/') ||
        window.location.pathname.startsWith('/store/') ||
        window.location.pathname.startsWith('/product/') ||
        window.location.pathname.startsWith('/blog') ||
        window.location.pathname.startsWith('/hakkimizda') ||
        window.location.pathname.startsWith('/yasal/') ||
        (process.env.REACT_APP_PUBLIC_SITE === 'true' && window.location.pathname === '/');
      const isPublicApiCall = error.config?.url?.includes('/public/') || 
                             error.config?.url?.includes('/quotes/public/') ||
                             error.config?.url?.includes('/storefront/') ||
                             error.config?.url?.includes('/locations/');
      const isLoginPage = window.location.pathname === '/login' || 
                         window.location.pathname === '/register';
      
      // Only redirect if not already redirecting, not on public/login pages, and not a public API call
      if (!isPublicPage && !isPublicApiCall && !isLoginPage && !isRedirecting) {
        isRedirecting = true;
        localStorage.removeItem('token');
        // Use replace to prevent back button issues
        window.location.replace('/login');
      }
    }
    return Promise.reject(error);
  }
);

// Giriş
export const login = async (credentials) => {
  const response = await api.post('/auth/login', credentials);
  return response.data;
};

// 2FA doğrulama
export const verifyTwoFactor = async (data) => {
  const response = await api.post('/auth/2fa/verify', data);
  return response.data;
};

// Kayıt
export const register = async (userData) => {
  const response = await api.post('/auth/register', userData);
  return response.data;
};

// Kullanıcı bilgilerini getir
export const getMe = async () => {
  const response = await api.get('/auth/me');
  return response.data;
};

// Kullanıcı davet et
export const inviteUser = async (inviteData) => {
  const response = await api.post('/auth/invite', inviteData);
  return response.data;
};

// Gerçek kullanıcıları getir
export const getUsers = async () => {
  const response = await api.get('/auth/users');
  return response.data;
};

// Kullanıcı detay
export const getUserDetail = async (userId) => {
  const response = await api.get(`/auth/users/${userId}`);
  return response.data;
};

// Kullanıcı güncelle
export const updateUser = async (userId, data) => {
  const response = await api.patch(`/auth/users/${userId}`, data);
  return response.data;
};

// Kullanıcıyı şirketten kaldır
export const deleteUser = async (userId) => {
  const response = await api.delete(`/auth/users/${userId}`);
  return response.data;
};

// Davetleri getir
export const getInvites = async () => {
  const response = await api.get('/auth/invites');
  return response.data;
};

// Kullanıcıya gelen davetler
export const getMyInvites = async () => {
  const response = await api.get('/auth/my-invites');
  return response.data;
};

// Davet detaylarını getir
export const getInviteDetails = async (inviteId) => {
  const response = await api.get(`/auth/invites/${inviteId}`);
  return response.data;
};

// Davet sil
export const deleteInvite = async (inviteId) => {
  const response = await api.delete(`/auth/invites/${inviteId}`);
  return response.data;
};

// Davet kabul et
export const acceptInvitation = async (data) => {
  const response = await api.post('/auth/accept-invitation', data);
  return response.data;
};

// Davet token'ını doğrula
export const validateInviteToken = async (token) => {
  const response = await api.get(`/auth/validate-invite?token=${token}`);
  return response.data;
};

// Kullanıcının bağlı olduğu şirketler
export const getMyCompanies = async () => {
  const response = await api.get('/auth/my-companies');
  return response.data;
};

// Aktif şirketi değiştir
export const switchCompany = async (companyId) => {
  const response = await api.post('/auth/switch-company', { companyId });
  return response.data;
};

export const authService = {
  login,
  verifyTwoFactor,
  register,
  getMe,
  getUsers,
  getUserDetail,
  updateUser,
  deleteUser,
  inviteUser,
  getInvites,
  getMyInvites,
  getInviteDetails,
  deleteInvite,
  acceptInvitation,
  validateInviteToken,
  getMyCompanies,
  switchCompany
}; 